import {
  OktonConfirmDialog,
  OktonDateFilter,
  OktonLoadingCard,
  OktonErrorCard,
  OktonSearchSelect,
  OktonPageHeader,
  OktonSearchFilter,
  OktonSegmentedFilter,
  OktonDataGrid,
  OktonEmptyState,
  ColunaGrid,
  useGrid,
  TAMANHOS_PAGINA,
} from '@okton/platform-ui';
import {
  useToast,
  Badge,
  Box,
  Button,
  Flex,
  IconButton,
  Text,
  useColorModeValue,
} from '@chakra-ui/react';
import { Banknote, Plus, Trash2 } from 'lucide-react';
import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';
import BankAppPage from '../../layout/BankAppPage';
import { useContasBancarias } from '../../services/hooks/useContasBancarias';
import {
  Saque,
  useDeleteSaque,
  useSaques,
} from '../../services/hooks/useSaques';
import { formatCustomDate } from '../../utils/formatCustomDate';
import formatValue from '../../utils/formatValue';
import { FiltroData, setaFiltroAnual } from '../../utils/setaFiltroDiario';
import { colunasDataGrid } from '../../utils/colunasDataGrid';

const COLUNAS: ColunaGrid<Saque>[] = [
  { chave: 'data_saq', titulo: 'Data', valor: s => s.data_saq, tipo: 'data' },
  { chave: 'descricao_cts', titulo: 'Conta', valor: s => s.descricao_cts },
  {
    chave: 'nomemoedabanco_saq',
    titulo: 'Moeda Banco',
    valor: s => s.nomemoedabanco_saq,
  },
  { chave: 'nome_pct', titulo: 'Plano de Contas', valor: s => s.nome_pct },
  {
    chave: 'observacao_saq',
    titulo: 'Observação',
    valor: s => s.observacao_saq,
  },
  {
    chave: 'conciliado_saq',
    titulo: 'Status',
    valor: s => (s.conciliado_saq !== '0' ? 'Conciliado' : 'Pendente'),
  },
  {
    chave: 'valor_saq',
    titulo: 'Valor',
    valor: s => Number(s.valor_saq),
    tipo: 'numero',
    alinhamento: 'right',
  },
];

const Saques = () => {
  const history = useHistory();
  const bg = useColorModeValue('white', 'gray.800');
  const color = useColorModeValue('#3f3f46', '#d4d4d8');

  const [filtroData, setFiltroData] = useState<FiltroData>(setaFiltroAnual());
  const [filterAccount, setFilterAccount] = useState('');
  const [filterStatus, setFilterStatus] = useState<
    'all' | 'pending' | 'reconciled'
  >('all');

  const { data: contas = [] } = useContasBancarias();
  const {
    data: saques = [],
    isLoading,
    isError,
    refetch,
  } = useSaques(
    filterAccount || undefined,
    filtroData[1],
    filtroData[2],
    filterStatus,
  );
  const deleteMutation = useDeleteSaque();

  const toast = useToast();
  const [itemExcluir, setItemExcluir] = useState<string | null>(null);
  const [excluindo, setExcluindo] = useState<string | null>(null);
  const [busca, setBusca] = useState('');
  const {
    ordenacao,
    alternarOrdenacao,
    itens: visiveis,
    filtrados,
    pagina,
    setPagina,
    tamanhoPagina,
    setTamanhoPagina,
  } = useGrid(saques, COLUNAS, busca);

  const remove = async () => {
    if (!itemExcluir) return;
    setExcluindo(itemExcluir);
    try {
      await deleteMutation.mutateAsync(itemExcluir);
      setItemExcluir(null);
      refetch();
    } catch (err) {
      toast({
        title: err instanceof Error ? err.message : 'Erro ao excluir o Saque.',
        status: 'error',
        position: 'bottom-right',
        isClosable: true,
      });
    } finally {
      setExcluindo(null);
    }
  };

  return (
    <BankAppPage>
      <OktonPageHeader
        eyebrow="CADASTROS"
        title="Saques"
        subtitle="Retiradas da Conta Bancária"
        actions={
          <>
            <OktonDateFilter
              value={{
                preset: filtroData[0],
                start: filtroData[1],
                end: filtroData[2],
              }}
              onChange={next =>
                setFiltroData([next.preset, next.start, next.end])
              }
            />
          </>
        }
      />

      <Flex minH="0" w="100%" my="3" mt="4" gridGap={2} flexWrap="wrap">
        <OktonSegmentedFilter<typeof filterStatus>
          value={filterStatus}
          onChange={setFilterStatus}
          options={[
            { value: 'all', label: 'Todos' },
            { value: 'pending', label: 'Pendentes' },
            { value: 'reconciled', label: 'Conciliados' },
          ]}
        />
        <OktonSearchFilter
          value={busca}
          onChange={setBusca}
          placeholder="Buscar por conta, plano, observação..."
        />
        <Flex
          borderWidth="1px"
          ml="auto"
          p="2"
          rounded="2xl"
          bg={bg}
          align="center"
          gridGap={2}
        >
          <Button
            leftIcon={<Plus size={18} />}
            colorScheme="blue"
            onClick={() => history.push('/saques/novo')}
            minW="150px"
          >
            Novo Saque
          </Button>
          <OktonSearchSelect
            name="filtroconta"
            placeholder="Todas as contas"
            value={filterAccount}
            onChange={valor => setFilterAccount(valor)}
            options={[
              ...contas.map(c => ({
                value: c.id_cts,
                label: c.descricao_cts,
              })),
            ]}
          />
        </Flex>
      </Flex>

      {isLoading ? (
        <Flex mt="1">
          <OktonLoadingCard h="200px" />
        </Flex>
      ) : isError ? (
        <Flex mt="1">
          <OktonErrorCard h="200px" onRetry={refetch} />
        </Flex>
      ) : (
        <Box bg={bg} mt="1" rounded="2xl">
          {filtrados.length === 0 ? (
            <OktonEmptyState
              icon={Banknote}
              title={
                busca
                  ? 'Nada encontrado para a busca'
                  : 'Nenhum saque no período'
              }
            />
          ) : (
            <OktonDataGrid
              rows={visiveis}
              columns={[
                ...colunasDataGrid(COLUNAS, ordenacao, alternarOrdenacao, {
                  data_saq: s => (
                    <Box color="gray.600" whiteSpace="nowrap">
                      {formatCustomDate(
                        s.data_saq.slice(0, 10),
                        'yyyy-MM-dd',
                        'dd/MM/yyyy',
                      )}
                    </Box>
                  ),
                  descricao_cts: s => (
                    <Box color={color} fontWeight="bold">
                      {s.descricao_cts ?? '—'}
                    </Box>
                  ),
                  nomemoedabanco_saq: s => (
                    <Box color="gray.500">{s.nomemoedabanco_saq ?? '—'}</Box>
                  ),
                  nome_pct: s => (
                    <>
                      {s.nome_pct ? (
                        <Badge colorScheme="blue" borderRadius="full" px="2">
                          {s.nome_pct}
                        </Badge>
                      ) : (
                        <Text color="gray.400">—</Text>
                      )}
                    </>
                  ),
                  observacao_saq: s => (
                    <Box color="gray.500" maxW="260px">
                      {s.observacao_saq ?? '—'}
                    </Box>
                  ),
                  conciliado_saq: s => (
                    <>
                      {s.conciliado_saq !== '0' ? (
                        <Badge colorScheme="green" borderRadius="full" px="2">
                          Conciliado
                        </Badge>
                      ) : (
                        <Badge colorScheme="orange" borderRadius="full" px="2">
                          Pendente
                        </Badge>
                      )}
                    </>
                  ),
                  valor_saq: s => (
                    <Box
                      fontWeight="semibold"
                      whiteSpace="nowrap"
                      color="red.500"
                    >
                      {formatValue(-Number(s.valor_saq))}
                    </Box>
                  ),
                }),
                {
                  key: 'acoes',
                  header: null,
                  align: 'right',
                  render: s => (
                    <Flex justify="flex-end" gridGap="1">
                      {s.conciliado_saq === '0' && (
                        <IconButton
                          aria-label="Excluir"
                          size="sm"
                          variant="ghost"
                          colorScheme="red"
                          icon={<Trash2 size={16} />}
                          onClick={() => setItemExcluir(s.id_saq)}
                        />
                      )}
                    </Flex>
                  ),
                },
              ]}
              getRowId={s => s.id_saq}
              page={pagina + 1}
              pageSize={tamanhoPagina}
              total={filtrados.length}
              pageSizeOptions={TAMANHOS_PAGINA}
              onPageChange={nova => setPagina(nova - 1)}
              onPageSizeChange={setTamanhoPagina}
            />
          )}
        </Box>
      )}
      <OktonConfirmDialog
        isOpen={Boolean(itemExcluir)}
        onClose={() => {
          if (!excluindo) setItemExcluir(null);
        }}
        onConfirm={remove}
        title="Confirmar exclusão"
        description="Excluir este Saque do IFLEX?"
        confirmLabel="Excluir"
        intent="danger"
        isLoading={Boolean(excluindo)}
      />
    </BankAppPage>
  );
};

export default Saques;
